import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Play, Loader, AlertTriangle, GitBranch, FlaskConical, CheckCircle, XCircle } from 'lucide-react';

import { getBQTablePreview, launchPatternBulkTreatment, PatternRule, PatternStep, testProcessSingleRow, ProcessSingleRowResponse } from 'services/apiService';

interface PatternPreviewPanelProps {
  projectId: string;
  config: BigQueryConfig;
  selectedColumns: string[];
  imageColumn: string | null;
  patternRules: PatternRule[];
  patternPreviews: Record<string, PatternStep[]>;  // Added: steps for each rule
  treatmentName: string;
  onBack: () => void;
}

interface BigQueryConfig {
  project_id: string;
  dataset_id: string;
  table_name: string;
}

interface PreviewRow {
  [key: string]: any;
}

const PatternPreviewPanel: React.FC<PatternPreviewPanelProps> = ({
  projectId,
  config,
  selectedColumns,
  imageColumn,
  patternRules,
  patternPreviews,  // Now using this prop
  treatmentName,
  onBack
}) => {
  const navigate = useNavigate();
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState<number>(0);
  const [matchingRowCount, setMatchingRowCount] = useState<number>(0);
  
  // Test preview state
  const [testingRowIndex, setTestingRowIndex] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<Record<number, ProcessSingleRowResponse>>({});

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Convert pattern rules to prompt-style rules for preview (just for row matching)
      const previewRules: PromptRule[] = patternRules.map(rule => ({
        id: rule.id,
        condition: {
          column: rule.condition.column,
          operator: rule.condition.operator as PromptRule['condition']['operator'],
          value: rule.condition.value
        },
        prompt: `Pattern: ${rule.pattern_config.from_picture_id} → ${rule.pattern_config.to_picture_id}`
      }));

      const data = await getBQTablePreview(
        config.project_id,
        config.dataset_id,
        config.table_name,
        selectedColumns.concat(imageColumn ? [imageColumn] : []),
        previewRules,
        projectId
      );
      const matchingRows: any[] = data.rows || [];
      
      setPreviewData(matchingRows.slice(0, 5));
      setRowCount(data.total_rows_with_skipped || 0);
      setMatchingRowCount(data.total_rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [config.dataset_id, config.project_id, config.table_name, imageColumn, patternRules, projectId, selectedColumns]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const evaluateCondition = (row: PreviewRow, rule: PatternRule): boolean => {
    const { column, operator, value } = rule.condition;
    const rowValue = String(row[column] || '');
    
    switch (operator) {
      case '=':
        return rowValue === value;
      case '!=':
        return rowValue !== value;
      case 'contains':
        return rowValue.toLowerCase().includes(value.toLowerCase());
      case 'starts_with':
        return rowValue.toLowerCase().startsWith(value.toLowerCase());
      case 'ends_with':
        return rowValue.toLowerCase().endsWith(value.toLowerCase());
      case '>':
        return parseFloat(rowValue) > parseFloat(value);
      case '<':
        return parseFloat(rowValue) < parseFloat(value);
      case '>=':
        return parseFloat(rowValue) >= parseFloat(value);
      case '<=':
        return parseFloat(rowValue) <= parseFloat(value);
      default:
        return false;
    }
  };

  const getMatchingRule = (row: PreviewRow): PatternRule | null => {
    for (const rule of patternRules) {
      if (evaluateCondition(row, rule)) {
        return rule;
      }
    }
    return null;
  };

  const substituteVariables = (text: string, row: PreviewRow): string => {
    let result = text;
    selectedColumns.forEach(col => {
      const regex = new RegExp(`\\{${col}\\}`, 'g');
      result = result.replace(regex, String(row[col] || ''));
    });
    return result;
  };

  const handleTestRow = async (rowIndex: number, row: PreviewRow) => {
    setTestingRowIndex(rowIndex);
    setError(null);

    if (!imageColumn) {
      setError('Image column is required to test the pattern.');
      setTestingRowIndex(null);
      return;
    }

    try {
      // Include the steps from patternPreviews in the pattern rules for the backend
      const rulesWithSteps = patternRules.map(rule => ({
        ...rule,
        pattern_config: {
          ...rule.pattern_config,
          // Get steps from patternPreviews using the rule id
          steps: patternPreviews[rule.id] || []
        }
      }));

      const result = await testProcessSingleRow(
        row,
        rulesWithSteps,
        imageColumn,
        projectId
      );

      setTestResults(prev => ({
        ...prev,
        [rowIndex]: result
      }));
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [rowIndex]: {
          status: 'error',
          rule_id: '',
          error_message: err instanceof Error ? err.message : 'Test failed'
        }
      }));
    } finally {
      setTestingRowIndex(null);
    }
  };

  const handleLaunch = async () => {
    setLaunching(true);
    setError(null);

    if (!imageColumn) {
      setError('Image column is required to launch the treatment.');
      setLaunching(false);
      return;
    }

    try {
      await launchPatternBulkTreatment(
        config.project_id,
        config.dataset_id,
        config.table_name,
        selectedColumns,
        imageColumn,
        treatmentName,
        projectId,
        patternRules
      );

      navigate(`/projects/${projectId}/bulk-treatments`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to launch treatment');
    } finally {
      setLaunching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="w-8 h-8 text-blue-400 animate-spin mb-4" />
        <p className="text-gray-400">Loading preview...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Eye className="w-6 h-6 text-green-400" />
          <h2 className="text-xl font-semibold text-white">Preview & Launch (Pattern Mode)</h2>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-300 font-medium">Error</p>
            <p className="text-xs text-red-400 mt-1">{error}</p>
          </div>
        </div>
      )}

      {matchingRowCount === 0 && patternRules.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-300 font-medium">No matching rows</p>
            <p className="text-xs text-yellow-400 mt-1">
              No rows match your defined rules. Please adjust your conditions.
            </p>
          </div>
        </div>
      )}

      {/* Treatment Summary */}
      <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Treatment Summary</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Table:</span>
            <span className="text-white font-mono text-xs">
              {config.project_id}.{config.dataset_id}.{config.table_name}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Input Image Column:</span>
            <span className="text-purple-400 font-mono text-xs">
              {imageColumn || 'None'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Treatment Mode:</span>
            <span className="text-blue-400 flex items-center gap-1.5">
              <GitBranch className="w-3.5 h-3.5" />
              Pattern-Based
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Pattern Rules:</span>
            <span className="text-white">
              {patternRules.length} rule{patternRules.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Rows to process:</span>
            <span className="text-green-400 font-semibold">
              {matchingRowCount.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Pattern Rules Summary */}
      <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Pattern Rules</h3>
        <div className="space-y-3">
          {patternRules.map((rule, idx) => (
            <div key={rule.id} className="bg-black/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-purple-400">Rule {idx + 1}</span>
                <span className="text-xs px-2 py-1 bg-purple-500/20 border border-purple-500/30 text-purple-300 rounded">
                  {rule.condition.column} {rule.condition.operator} "{rule.condition.value}"
                </span>
              </div>
              <div className="text-xs text-gray-400">
                <span className="text-gray-500">Pattern:</span>{' '}
                <span className="font-mono text-blue-300">
                  {rule.pattern_config.from_picture_id} → {rule.pattern_config.to_picture_id}
                </span>
              </div>
              {Object.keys(rule.pattern_config.prompt_overrides || {}).length > 0 && (
                <div className="text-xs text-gray-400 mt-1">
                  <span className="text-gray-500">Customizations:</span>{' '}
                  {Object.keys(rule.pattern_config.prompt_overrides || {}).length} step(s) with prompt overrides
                </div>
              )}
              {(rule.pattern_config.additionnal_pictures || []).length > 0 && (
                <div className="text-xs text-gray-400 mt-1">
                  <span className="text-gray-500">Additional images:</span>{' '}
                  {(rule.pattern_config.additionnal_pictures || []).length} reference image(s)
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Preview Examples - Updated with Test Button */}
      <div>
        <h3 className="text-sm font-semibold text-gray-300 mb-3">
          Preview ({previewData.length} examples) - Click "Test" to process a row
        </h3>
        {previewData.length === 0 ? (
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-8 text-center">
            <p className="text-gray-400">No matching rows</p>
          </div>
        ) : (
          <div className="space-y-3">
            {previewData.map((row, idx) => {
              const matchingRule = getMatchingRule(row);
              if (!matchingRule) return null;
              
              const imageUrl = imageColumn ? row[imageColumn] : null;
              const testResult = testResults[idx];
              const isTesting = testingRowIndex === idx;
              
              return (
                <div 
                  key={idx}
                  className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors"
                >
                  {/* Header with Test Button */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-500 font-semibold">ROW {idx + 1}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1 bg-purple-500/20 border border-purple-500/30 text-purple-300 rounded font-mono">
                        {matchingRule.condition.column} = "{matchingRule.condition.value}"
                      </span>
                      <button
                        onClick={() => handleTestRow(idx, row)}
                        disabled={isTesting || testingRowIndex !== null}
                        className="text-xs px-3 py-1.5 bg-yellow-500/20 border border-yellow-500/30 text-yellow-300 rounded hover:bg-yellow-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                      >
                        {isTesting ? (
                          <>
                            <Loader className="w-3 h-3 animate-spin" />
                            Testing...
                          </>
                        ) : (
                          <>
                            <FlaskConical className="w-3 h-3" />
                            Test
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="grid grid-cols-12 gap-4">
                    {/* Left: Input Image */}
                    {imageUrl && (
                      <div className="col-span-12 md:col-span-3">
                        <div className="bg-black/30 rounded-lg p-2 h-full">
                          <div className="text-xs text-purple-400 mb-2 font-medium">📷 Input</div>
                          <img 
                            src={String(imageUrl)} 
                            alt="Input Preview" 
                            className="w-full aspect-square object-cover rounded border border-gray-600"
                            onError={(e) => {
                              const target = e.currentTarget;
                              target.style.display = 'none';
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Middle: Test Result (if available) */}
                    {testResult && (
                      <div className="col-span-12 md:col-span-3">
                        <div className={`bg-black/30 rounded-lg p-2 h-full border ${
                          testResult.status === 'success' ? 'border-green-500/30' : 
                          testResult.status === 'error' ? 'border-red-500/30' : 'border-yellow-500/30'
                        }`}>
                          <div className={`text-xs mb-2 font-medium flex items-center gap-1.5 ${
                            testResult.status === 'success' ? 'text-green-400' : 
                            testResult.status === 'error' ? 'text-red-400' : 'text-yellow-400'
                          }`}>
                            {testResult.status === 'success' ? (
                              <><CheckCircle className="w-3 h-3" /> Output</>
                            ) : testResult.status === 'error' ? (
                              <><XCircle className="w-3 h-3" /> Error</>
                            ) : (
                              <><AlertTriangle className="w-3 h-3" /> Skipped</>
                            )}
                          </div>
                          {testResult.status === 'success' && testResult.output_base64 ? (
                            <img 
                              src={`data:image/png;base64,${testResult.output_base64}`}
                              alt="Output Preview" 
                              className="w-full aspect-square object-cover rounded border border-green-600"
                            />
                          ) : (
                            <div className="w-full aspect-square flex items-center justify-center bg-gray-800 rounded border border-gray-600">
                              <p className="text-xs text-gray-400 text-center px-2">
                                {testResult.error_message || 'No output'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Right: Data & Pattern Info */}
                    <div className={`${imageUrl ? (testResult ? 'col-span-12 md:col-span-6' : 'col-span-12 md:col-span-9') : 'col-span-12'} space-y-3`}>
                      {/* Column Values */}
                      {selectedColumns.length > 0 && (
                        <div className="bg-black/20 rounded-lg p-3">
                          <div className="text-xs text-gray-400 font-medium mb-2">Data</div>
                          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
                            {selectedColumns.map(col => (
                              <div key={col} className="flex items-start gap-1">
                                <span className="text-gray-500 shrink-0">{col}:</span>
                                <span className="text-gray-200 font-mono truncate" title={String(row[col] || 'N/A')}>
                                  {String(row[col] || 'N/A')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Pattern to Apply */}
                      <div className="bg-black/20 rounded-lg p-3">
                        <div className="text-xs text-gray-400 font-medium mb-2">Pattern to Apply</div>
                        <div className="text-sm text-blue-300 font-mono">
                          {matchingRule.pattern_config.pattern_thread_id}: {matchingRule.pattern_config.from_picture_id} → {matchingRule.pattern_config.to_picture_id}
                        </div>
                        
                        {/* Show customized prompts preview */}
                        {Object.entries(matchingRule.pattern_config.prompt_overrides || {}).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-700">
                            <div className="text-xs text-gray-500 mb-1">Prompt customizations:</div>
                            {Object.entries(matchingRule.pattern_config.prompt_overrides || {}).map(([stepIdx, overrides]) => (
                              <div key={stepIdx} className="text-xs text-gray-400">
                                Step {parseInt(stepIdx) + 1}: {(overrides as any[]).map((o: any) => 
                                  `"${o.find}" → "${substituteVariables(o.replace, row)}"`
                                ).join(', ')}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Launch Button */}
      <div className="flex gap-3 pt-4">
        <button
          onClick={onBack}
          disabled={launching}
          className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        <button
          onClick={handleLaunch}
          disabled={launching || matchingRowCount === 0}
          className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-green-500/40 hover:scale-[1.02] active:scale-[0.98] disabled:from-gray-700 disabled:to-gray-600 disabled:shadow-none disabled:cursor-not-allowed disabled:scale-100 flex items-center justify-center gap-2"
        >
          {launching ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Launching...
            </>
          ) : (
            <>
              <Play className="w-5 h-5" />
              Launch Pattern Treatment
            </>
          )}
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <p className="text-sm text-blue-300">
          <strong>Note:</strong> This treatment will process {matchingRowCount.toLocaleString()} images
          using pattern-based transformations in the background.
          {rowCount - matchingRowCount > 0 && ` ${(rowCount - matchingRowCount).toLocaleString()} rows that don't match any rule will be skipped.`}
          {' '}You'll receive a notification when it's complete.
        </p>
      </div>
    </div>
  );
};

export default PatternPreviewPanel;
